// ══════════════════════════════════════════════════════════════════════════════════════════════
//  EL SOPORTE DE BIANGO ESTÁ DENTRO DE TU CLÍNICA — el aviso, y el botón de cortarlo
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  QUÉ ES ESTO (desde el 1-oct-2026, B-998). El soporte de Biango puede entrar en la clínica para ayudar, sin pedirle
//  permiso a nadie: lo cubre el contrato de encargado del tratamiento. Lo que la clínica conserva es SABERLO y poder
//  CORTARLO: mientras alguien de Biango tiene una sesión abierta aquí, esta banda lo dice en todas las pantallas —quién,
//  con qué motivo y hasta cuándo— y cualquiera del equipo puede cerrarla. Cada entrada queda además en el Registro de
//  actividad de la clínica.
//
//  LO QUE SE HA RETIRADO. Hasta el 1-oct esta banda era también donde la clínica AUTORIZABA o RECHAZABA la petición del
//  soporte («vale la primera persona que conteste»). Ya no se pide permiso, así que no hay nada que autorizar: las dos
//  tarjetas de petición y sus dos botones se han ido, con sus lecturas.
//
//  ── POR QUÉ ES UNA FRANJA ARRIBA, SIEMPRE A LA VISTA, Y NO UNA PASTILLA EN UNA ESQUINA ────────
//  Porque lo que se cuenta aquí es que alguien de fuera está viendo las fichas, el historial, las radiografías y los
//  cobros de los pacientes de esta clínica: es la garantía de la clínica. Un contador sobre una campanita se aprende a
//  ignorar en dos días; y un dato que no se ve es un dato que no existe.
//  HASTA EL 3-OCT-2026 era una tarjeta grande pegada (`position: sticky`) DENTRO de lo que se desplaza: en un móvil se
//  comía media pantalla y se ponía encima de los botones (el dueño, de la banda gemela del soporte: «se queda siempre
//  sticky, y no me deja ver la pantalla ni trabajar»). AHORA, como components/DentroDeClinica: una franja de UNA línea
//  por aviso («El soporte de Biango (su nombre) está dentro · Cortar») que los layouts montan entre la cabecera y el
//  contenido, fuera de lo que se desplaza. Siempre se ve, empuja el contenido en vez de taparlo (sin `position` ni
//  z-index) y respeta la muesca a los lados. Con su flecha se despliega el aviso entero —quién, con qué motivo, qué
//  puede y qué no, hasta cuándo—, con tope de alto. La de «está dentro» nace plegada; las de «no se ha podido saber»,
//  «no disponible» y la respuesta a un clic nacen desplegadas: son raras y hay que leerlas.
//
//  ── LAS REGLAS QUE ESTE FICHERO NO PUEDE ROMPER ──────────────────────────────────────────────
//
//  A · LA PANTALLA NO OFRECE LO QUE EL SERVIDOR NIEGA NI NIEGA LO QUE CONCEDE. Cortar NO se esconde por rol: el servidor
//      deja cortar a CUALQUIER usuario activo de la clínica, así que una recepcionista ve el mismo botón que la
//      administradora.
//
//  B · LA RESPUESTA DEL SERVIDOR NO SE TIRA. Si mientras tienes el aviso abierto lo corta otra persona, tu clic contesta
//      409 con la fila DENTRO: se guarda y se pinta, de modo que la banda cuenta lo que ha pasado en vez de dejar un error
//      rojo sin salida.
//
//  C · EL RELOJ DEL NAVEGADOR NO DECIDE NADA. Se enseña la hora a la que se apaga la sesión (la del servidor, ya
//      formateada) y, como ayuda, cuánto falta; pero el botón no se apaga ni se esconde por esa cuenta.
//
//  D · NADA DE `confirm()`. Cortar abre el diálogo propio de la casa (context/DialogosContext).
//
//  E · UNA SALIDA QUE SE ESCRIBE AQUÍ TIENE QUE ABRIR PARA QUIEN LA LEE. El Registro de actividad es de la
//      administración de la clínica (`RoleRoute roles={['admin']}`, y su `GET /acceso-clinica/historial` contesta 403 a
//      los demás): la tarjeta nombra esa puerta SOLO a quien el servidor deja abrirla, y el criterio se PREGUNTA
//      (`services/permisosDelServidor`), nunca se copia. A todos los demás se les cuenta lo que SÍ pueden hacer.
import { useState, useRef, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useEsAdminDeLaClinica } from '../services/permisosDelServidor'
import { useIsMobile } from '../hooks/useIsMobile'
import { confirmar } from '../context/DialogosContext'
import { msgError } from '../utils/errores'
import { Ico } from './ui'
import { avisoDeLectura } from '../utils/lecturaSoporte'
import { t as i18nT, tx } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// El nombre del que pide, para leerlo en una línea. El correo va siempre detrás: dos personas del
// soporte pueden llamarse igual, y la cuenta es lo que queda escrito en el registro.
export function quienPide(f) {
  const n = (f && f.agente_nombre ? String(f.agente_nombre).trim() : '')
  const c = (f && f.agente_email ? String(f.agente_email).trim() : '')
  if (n && c) return n + ' (' + c + ')'
  return n || c || i18nT('components.AccesoSoporteBanner.alguien_del_soporte_tecnico', 'Alguien del soporte técnico')
}

// ── CUÁNTO QUEDA, A PARTIR DE LA CADENA DEL SERVIDOR ─────────────────────────────────────────
// El servidor manda 'DD/MM/YYYY HH:MM' ya formateado con to_char (nunca un TIMESTAMP crudo, que el
// driver devolvería corrido). Aquí se parte por trozos y se construye la fecha LOCAL con esos
// números: ni `new Date('...')`, ni `toISOString`, ni ninguna conversión de zona — las dos cosas que
// en esta casa restan un día o dos horas.
//
// Devuelve minutos (puede ser negativo) o `null` si la cadena no es la esperada. `null` significa
// «no lo sé», y entonces la pantalla no dice nada de tiempo restante: no se inventa un número.
export function minutosHasta(txt, ahora) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})[ T](\d{2}):(\d{2})/.exec(String(txt || ''))
  if (!m) return null
  const d = new Date(+m[3], +m[2] - 1, +m[1], +m[4], +m[5], 0, 0)
  const ref = ahora instanceof Date ? ahora : new Date()
  return Math.round((d.getTime() - ref.getTime()) / 60000)
}

export function enPalabras(min) {
  if (min == null) return null
  if (min <= 0) return i18nT('components.AccesoSoporteBanner.ya_deberia_haber_caducado', 'ya debería haber caducado')
  if (min < 60) return i18nT('components.AccesoSoporteBanner.plural_dentro_de_minutos', { one: 'dentro de {{count}} minuto', other: 'dentro de {{count}} minutos' }, { count: min, _forma: (min === 1) ? 'one' : 'other' })
  const h = Math.floor(min / 60), r = min % 60
  return i18nT('components.AccesoSoporteBanner.dentro_de_h', 'dentro de {{h}} h{{x}}', { h, x: (r ? i18nT('components.AccesoSoporteBanner.y_min', ' y {{r}} min', { r }) : '') })
}

// Hora y «cuánto falta» juntos, que es como se lee de un vistazo. La hora SIEMPRE (es el dato del
// servidor); el «dentro de…» solo si se ha sabido calcular.
function cuandoCaduca(txt) {
  const p = enPalabras(minutosHasta(txt))
  return txt ? (p ? txt + ' (' + p + ')' : txt) : null
}

const TONO = {
  vivo:       { borde: '#1d4ed8', fondo: 'rgba(29,78,216,0.10)',  titulo: '#1d4ed8' },
  emergencia: { borde: '#b91c1c', fondo: 'rgba(220,38,38,0.12)',  titulo: '#b91c1c' },
  hecho:      { borde: '#15803d', fondo: 'rgba(22,163,74,0.10)',  titulo: '#15803d' },
  aviso:      { borde: '#64748b', fondo: 'var(--surface-2)',      titulo: 'var(--text-secondary)' },
  fallo:      { borde: '#b91c1c', fondo: 'rgba(220,38,38,0.12)',  titulo: '#b91c1c' },
}

// EL TITULO DE LA NOTA LO DECIDE LO QUE CONTESTO EL SERVIDOR, y son TRES cosas distintas. Estaba
// escrito con un solo ternario y eso convertia CUALQUIER fallo —un 500, la red caida, un 404— en «ya
// estaba cerrado», que es una afirmacion FALSA sobre un acceso a los datos de los pacientes. Llegar el
// segundo (409) y no haber podido hacerlo son cosas opuestas: una no hay que repetirla y la otra si.
const TITULO_NOTA = {
  get hecho() { return i18nT('components.AccesoSoporteBanner.hecho', 'Hecho') },
  get aviso() { return i18nT('components.AccesoSoporteBanner.ese_acceso_ya_estaba_cerrado', 'Ese acceso ya estaba cerrado') },
  get fallo() { return i18nT('components.AccesoSoporteBanner.no_se_ha_podido', 'No se ha podido') },
}
const ICONO_NOTA = { hecho: 'check', aviso: 'info', fallo: 'warn' }

// ── UNA FRANJA: la línea (icono, frase, flecha y su botón) y, desplegada, el aviso entero ──────
//  EN FLUJO, SIN `position` NI z-index: es lo que la hace empujar el contenido y no taparlo. La línea no se parte (si no
//  cabe, puntos suspensivos) y respeta la muesca a los dos lados, como la cabecera que tiene encima. SIN HOOKS a
//  propósito: si está desplegada lo decide el componente de abajo (`abiertas`), y así esta función se puede pintar sola.
function Franja({ tono = 'aviso', icono = 'warn', linea, titulo, children, accion, movil, marca, lectura, abierta, alAlternar, idDetalle }) {
  const t = TONO[tono] || TONO.aviso
  const lado = movil ? 12 : 24
  return (
    <div data-acceso-soporte={tono} data-acceso-lectura={lectura} style={{
      background: t.fondo, borderBottom: '1px solid ' + t.borde, borderLeft: '4px solid ' + t.borde, color: 'var(--text-primary)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: movil ? '8px' : '10px', minHeight: movil ? '36px' : '34px',
        padding: '3px calc(' + lado + 'px + env(safe-area-inset-right, 0px)) 3px calc(' + (lado - 4) + 'px + env(safe-area-inset-left, 0px))' }}>
        <Ico n={icono} s={movil ? 15 : 16} c={t.titulo} />
        <span data-acceso-linea style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          fontSize: movil ? '12.5px' : '13px', fontWeight: 700, color: t.titulo, lineHeight: 1.3 }}>
          {linea}
        </span>
        <button type="button" data-acceso-desplegar aria-expanded={abierta ? 'true' : 'false'} aria-controls={idDetalle}
          aria-label={movil ? (abierta ? i18nT('components.AccesoSoporteBanner.ocultar_el_aviso_entero', 'Ocultar el aviso entero') : i18nT('components.AccesoSoporteBanner.leer_el_aviso_entero', 'Leer el aviso entero')) : undefined}
          title={abierta ? i18nT('components.AccesoSoporteBanner.ocultar_el_aviso_entero', 'Ocultar el aviso entero') : i18nT('components.AccesoSoporteBanner.leer_el_aviso_entero', 'Leer el aviso entero')} onClick={alAlternar} style={boton({ tipo: 'flecha', movil })}>
          {!movil && (abierta ? i18nT('components.AccesoSoporteBanner.ocultar', 'Ocultar') : i18nT('components.AccesoSoporteBanner.ver_detalle', 'Ver detalle'))}
          <Ico n={abierta ? 'chevronU' : 'chevronD'} s={14} />
        </button>
        {accion}
      </div>
      {abierta && (
        <div id={idDetalle} data-acceso-detalle style={{
          padding: '2px calc(' + lado + 'px + env(safe-area-inset-right, 0px)) 10px calc(' + (lado + 20) + 'px + env(safe-area-inset-left, 0px))',
          fontSize: movil ? '12.5px' : '13px', color: 'var(--text-primary)', lineHeight: 1.6,
          maxHeight: 'calc(var(--alto-pantalla) * 0.4)', overflowY: 'auto',
        }}>
          {(titulo || marca) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '3px' }}>
              {titulo && <span style={{ fontWeight: 800, color: t.titulo }}>{titulo}</span>}
              {marca && (
                <span style={{ background: '#1d4ed8', color: '#fff', borderRadius: '6px', padding: '2px 8px',
                               fontSize: '10.5px', fontWeight: 700, whiteSpace: 'nowrap' }}>{i18nT('components.AccesoSoporteBanner.soporte_de_biango', 'Soporte de Biango')}</span>
              )}
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  )
}

// Los botones de la línea: pequeños, de una palabra si se puede, sin partirse.
function boton({ tipo, movil }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px', flexShrink: 0,
    padding: movil ? '5px 10px' : '4px 12px', minHeight: '28px', borderRadius: '7px', cursor: 'pointer',
    fontSize: '12.5px', fontWeight: 800, border: '1px solid transparent', whiteSpace: 'nowrap',
  }
  if (tipo === 'no') return { ...base, background: 'var(--surface)', color: 'var(--text-primary)', borderColor: 'var(--border)' }
  if (tipo === 'corta') return { ...base, background: '#b91c1c', color: '#fff' }
  if (tipo === 'flecha') return { ...base, background: 'transparent', color: 'var(--text-primary)', borderColor: 'var(--border)' }
  return base
}

// El nombre que cabe en la línea: el de la persona; su cuenta va en el aviso entero.
function nombreCorto(f) {
  const n = (f && f.agente_nombre ? String(f.agente_nombre).trim() : '')
  return n || (f && f.agente_email ? String(f.agente_email).trim() : '')
}

export default function AccesoSoporteBanner() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const movil = useIsMobile(640)
  // LO QUE CONTESTÓ EL SERVIDOR A MI ÚLTIMO CLIC, por id de acceso: { fila, mensaje, tono }.
  // Aquí vive la regla B: la fila que devuelve el servidor —también la que viene DENTRO de un 409—
  // se guarda y se pinta. Sin esto, quien llega segundo se queda con un error rojo y sin saber que
  // el acceso ya lo había cortado una compañera.
  const [respuestas, setRespuestas] = useState({})
  const [ocupado, setOcupado] = useState(null)
  const desdeTransitorio = useRef({})   // B-471: desde cuando falla cada lectura por un reinicio
  // B-683 · EL QUIOSCO NO PINTA ESTA BANDA. Es una cuenta COMPARTIDA que vive abierta en el totem de la
  // entrada, en modo fichar: quien pasa por delante no tiene por que cortar el acceso de nadie —ni
  // debe—, asi que un aviso ahi no avisa a quien tiene que reaccionar; solo asusta a la gente que
  // llega a trabajar. A quien SI puede hacer algo se la sigue pintando en su pantalla, igual que hoy.
  const esQuiosco = user?.rol === 'control_horario'
  // B-683 · Y NO HAY INTERNET NO ES UNA SEÑAL PERDIDA. Se mira el estado real del navegador, con sus
  // dos avisos, y se vuelve a leer en cuanto hay linea.
  const [sinConexion, setSinConexion] = useState(typeof navigator !== 'undefined' && navigator.onLine === false)
  // LO QUE HA DESPLEGADO O PLEGADO ESTA PERSONA, por franja ('vivo-<id>', 'aviso-<clase>', 'nota-<id>'). Lo que no ha
  // tocado se queda como nace (ver la cabecera): la de «está dentro», plegada; las demás, desplegadas.
  const [abiertas, setAbiertas] = useState({})
  const estaAbierta = (clave, deFabrica) => (Object.prototype.hasOwnProperty.call(abiertas, clave) ? abiertas[clave] === true : deFabrica)
  const alternar = (clave, deFabrica) => () => setAbiertas(a => ({ ...a, [clave]: !(Object.prototype.hasOwnProperty.call(a, clave) ? a[clave] === true : deFabrica) }))
  useEffect(() => {
    if (typeof window === 'undefined' || !window.addEventListener) return undefined
    const fuera = () => setSinConexion(true)
    const dentro = () => { setSinConexion(false); qc.invalidateQueries({ queryKey: ['acceso-soporte-vivos'] }) }
    window.addEventListener('offline', fuera); window.addEventListener('online', dentro)
    return () => { window.removeEventListener('offline', fuera); window.removeEventListener('online', dentro) }
  }, [qc])
  // ¿Puede ESTA persona abrir el Registro de actividad? Se lee CERRADO (mientras la respuesta no
  // está, es que no), que es el lado que no promete una puerta cerrada. Ver la regla E de arriba.
  const { esAdmin: puedeRevisarElRegistro } = useEsAdminDeLaClinica()

  // LA LECTURA, SIN `.catch(() => null)`. Ahi estuvo el fallo mas caro de este fichero: un 500,
  // un token caducado, la falta de la migracion y «no hay nadie» acababan los CUATRO en el mismo
  // `null`, y el `return null` de abajo los pintaba a los cuatro como el mismo pixel vacio. Con el
  // soporte DENTRO y la lectura rota, la banda pasaba de un boton «Cortar el acceso ahora» y el
  // texto «El soporte tecnico esta dentro de los datos de esta clinica» a CERO botones y pantalla
  // limpia: la clinica perdia a la vez el saberlo y el poder cortarlo, y sin un solo sintoma.
  // `retry: false` a proposito, igual que en su vecino AccesosSoporteClinica: el dato es «hay
  // alguien de fuera dentro de las fichas de tus pacientes» y no se puede tardar tres reintentos en
  // decir que no se ha podido leer.
  // ── EL SERVIDOR QUE SE ESTA REINICIANDO NO ES UNA LECTURA ROTA ────────────────────────────
  //
  //  Lo vio el dueño el 2-sep: cada vez que se despliega (el servidor se reinicia unos segundos),
  //  esta banda saltaba entera a TODA la clinica con su parrafo de «no se puede saber si el soporte
  //  tecnico esta dentro». Y no era verdad: era que el servidor no estaba levantado todavia. Un
  //  aviso grave que aparece solo cada vez que subimos codigo enseña a ignorarlo, que es justo lo
  //  contrario de lo que esta banda existe para conseguir.
  //
  //  Se distingue el fallo TRANSITORIO —no hay respuesta (el servidor no contesta), o contesta 502
  //  / 503 / 504, que son los de un proceso reiniciandose o un proxy sin nadie detras— del fallo de
  //  VERDAD (un 401, un 403, un 500 con cuerpo, la migracion que falta). Al transitorio se le dan
  //  cuatro intentos con espera creciente, unos 15 segundos en total: mas que de sobra para un
  //  reinicio, y si a los 15 segundos sigue sin poderse leer, entonces SI se dice — porque entonces
  //  ya no es un reinicio. El `retry: false` de siempre se conserva para todo lo demas: el dato es
  //  «hay alguien de fuera dentro de las fichas de tus pacientes» y ese no puede esperar reintentos.
  const esTransitorio = (e) => {
    const st = e && e.response && e.response.status
    return !(e && e.response) || st === 502 || st === 503 || st === 504
  }
  const reintentos = { retry: (n, e) => esTransitorio(e) && n < 4, retryDelay: (n) => Math.min(1000 * (2 ** n), 8000) }
  const { data: vivosResp, error: errorVivos } = useQuery({
    queryKey: ['acceso-soporte-vivos'],
    queryFn: () => api.get('/acceso-clinica/vivos').then(r => r.data),
    enabled: !!user,
    ...reintentos,
    refetchInterval: 45000,
  })

  const refrescar = () => {
    qc.invalidateQueries({ queryKey: ['acceso-soporte-vivos'] })
    qc.invalidateQueries({ queryKey: ['accesos-soporte-historial'] })
  }

  // TODA respuesta del servidor pasa por aquí, la buena y la de error. `fila` es lo que manda: si
  // viene, ese es el estado de verdad de ese acceso y con él se pinta.
  const guardar = (id, datos) => setRespuestas(r => ({ ...r, [id]: datos }))

  const llamar = async (id, ruta, cuerpo, alSalirBien) => {
    setOcupado(id)
    try {
      const r = await api.post(ruta, cuerpo || {})
      guardar(id, { fila: r.data && r.data.fila, mensaje: alSalirBien(r.data && r.data.fila), tono: 'hecho' })
    } catch (e) {
      const d = e && e.response && e.response.data
      guardar(id, {
        fila: d && d.fila,
        mensaje: msgError(e),
        // Un 409 NO es una avería: es la respuesta correcta a «llegaste el segundo». Se pinta como
        // información, no como un fallo rojo, porque nadie ha hecho nada mal.
        tono: (e && e.response && e.response.status === 409) ? 'aviso' : 'fallo',
      })
    } finally {
      setOcupado(null)
      refrescar()
    }
  }

  const revocar = async (f) => {
    const ok = await confirmar({
      titulo: i18nT('components.AccesoSoporteBanner.cortar_el_acceso_del_soporte', 'Cortar el acceso del soporte'),
      mensaje: i18nT('components.AccesoSoporteBanner.dejara_de_ver_los_datos', '{{quienPide}} dejará de ver los datos de esta clínica ahora mismo.', { quienPide: quienPide(f) }),
      detalle: i18nT('components.AccesoSoporteBanner.si_estaba_resolviendo_algo_a', 'Si estaba resolviendo algo a medias, avísale antes. Si hace falta, el soporte puede volver a entrar, y volverá a quedar apuntado en el Registro de actividad.'),
      textoConfirmar: i18nT('components.AccesoSoporteBanner.cortar_el_acceso', 'Cortar el acceso'),
      textoCancelar: i18nT('components.AccesoSoporteBanner.dejarlo', 'Dejarlo'),
      tipo: 'danger',
    })
    if (!ok) return
    await llamar(f.id, '/acceso-clinica/' + f.id + '/revocar', { clinicaId: f.clinica_id },
      () => i18nT('components.AccesoSoporteBanner.acceso_cortado_esa_sesion_del', 'Acceso cortado. Esa sesión del soporte ya no ve los datos de la clínica.'))
  }

  // LO VIVO: lo que dice el servidor, MÁS lo que acaba de contestar a este navegador. Las dos
  // fuentes son la misma cosa —una fila del servidor—, y unirlas es lo que evita que entre el clic y
  // el siguiente sondeo la banda diga «no hay nada abierto» justo después de haber abierto algo.
  const vivos = []
  const metidos = new Set()
  for (const f of ((vivosResp && vivosResp.filas) || [])) { if (f && f.vivo) { vivos.push(f); metidos.add(f.id) } }
  for (const k of Object.keys(respuestas)) {
    const f = respuestas[k] && respuestas[k].fila
    if (f && f.vivo === true && !metidos.has(f.id)) { vivos.push(f); metidos.add(f.id) }
  }
  // LAS NOTAS NO SE ESCONDEN PORQUE HAYA OTRO ACCESO VIVO: son dos cosas distintas, «qué ha pasado
  // con mi clic» y «qué hay abierto ahora mismo».
  const notas = Object.keys(respuestas).map(k => ({ id: k, ...respuestas[k] }))

  // ── LO QUE NO SE HA PODIDO SABER, DICHO CON EL MOTIVO DEL SERVIDOR ─────────────────────────
  //
  //  SON DOS COSAS DISTINTAS Y NO SE PUEDEN LEER IGUAL:
  //   · `error` (un 500, la red, un token caducado) → NO SE SABE. Puede haber alguien de fuera
  //     dentro de las fichas ahora mismo y esta pantalla no lo ve. Va en rojo, porque lo que se ha
  //     perdido es justo la señal que existe para no perderse nunca.
  //   · `disponible:false` → el servidor SI lo sabe y lo dice con todas las letras: sin esa
  //     migracion el soporte no entra en ninguna clinica, o sea que NO hay nadie dentro. Es una nota
  //     gris, no una alarma; pintarla de rojo seria asustar con lo contrario de lo que pasa.
  //  El motivo se PINTA, nunca se resume ni se sustituye por uno de la casa: el servidor escribe el
  //  nombre del fichero de la migracion que falta, y quien lea esto puede tener que reenviarlo.
  // B-471 · UN DESPLIEGUE LARGO TAMPOCO ES UNA LECTURA ROTA. Los reintentos de arriba cubren un
  // reinicio (~15 s), pero un despliegue instala y compila antes de levantar el proceso y la alarma
  // roja salia igual, a toda la clinica. Mientras el fallo siga siendo TRANSITORIO (esTransitorio) se
  // espera (utils/lecturaSoporte, GRACIA_REINICIO_MS) y, si pasado ese tiempo sigue, se dice. Lo que
  // NO es transitorio pasa tal cual y se dice YA. `desdeTransitorio` recuerda por lectura desde cuando
  // falla seguido; la consulta repinta sola cada 45 s (refetchInterval), asi que el aviso llega sin reloj.
  const errorQueSeDice = (que, error) => {
    const r = avisoDeLectura({ error, transitorio: !!error && esTransitorio(error),
      desde: desdeTransitorio.current[que] ?? null, ahora: Date.now(), sinConexion })
    desdeTransitorio.current[que] = r.desde
    return r.ciego ? error : null
  }
  const LECTURAS = [
    { que: 'vivos', error: errorQueSeDice('vivos', errorVivos), resp: vivosResp },
  ]
  const claseDe = (l) => (l.error ? 'ciego' : (l.resp && l.resp.disponible === false ? 'apagado' : null))
  const motivoDe = (l) => (l.error
    ? msgError(l.error)
    : String((l.resp && l.resp.error) || '').trim() || 'El servidor no ha dicho por qué.')
  const avisos = []
  for (const clase of ['ciego', 'apagado']) {
    const rotas = LECTURAS.filter(l => claseDe(l) === clase)
    if (!rotas.length) continue
    // UNA tarjeta por clase, no dos iguales seguidas: dos avisos identicos uno debajo del otro se
    // leen como uno solo y enseñan a saltarse la franja entera.
    avisos.push({
      id: clase,
      clase,
      motivo: [...new Set(rotas.map(motivoDe))].join(' · '),
    })
  }

  // Y AQUI ESTABA EL PIXEL VACIO. Antes se cortaba sin mirar los avisos, asi que «no hay nadie»
  // (la verdad) y «no he podido saberlo» (lo peligroso) salian exactamente igual: en blanco.
  if (!vivos.length && !notas.length && !avisos.length) return null

  if (esQuiosco) return null   // B-683
  return (
    // EN FLUJO, SIN `position` NI z-index (ver la cabecera): los layouts la montan entre la cabecera y el contenido, así
    // que siempre se ve y empuja lo de debajo en vez de taparlo. Los diálogos de la casa (99998) salen por encima igual.
    <div data-acceso-soporte-banda="1" style={{ flexShrink: 0 }}>
      {/* LO PRIMERO, ANTES QUE NADA. Si no se ha podido leer, todo lo que hay debajo —incluido no
          ver ninguna franja— significa otra cosa. */}
      {avisos.map(a => (
        <Franja key={a.id} lectura={a.clase} movil={movil}
          tono={a.clase === 'ciego' ? 'fallo' : 'aviso'}
          icono={a.clase === 'ciego' ? 'warn' : 'info'}
          linea={a.clase === 'ciego'
            ? i18nT('components.AccesoSoporteBanner.no_se_puede_saber_si', 'No se puede saber si el soporte técnico está dentro de los datos de esta clínica')
            : i18nT('components.AccesoSoporteBanner.el_registro_de_entradas_del', 'El registro de entradas del soporte técnico no está disponible en este servidor')}
          abierta={estaAbierta('aviso-' + a.id, true)} alAlternar={alternar('aviso-' + a.id, true)} idDetalle={'acceso-soporte-aviso-' + a.id}
          accion={a.clase === 'ciego'
            ? <button type="button" data-acceso-reintentar={a.id} onClick={refrescar} style={boton({ tipo: 'no', movil })}
                title={i18nT('components.AccesoSoporteBanner.volver_a_intentar_la_lectura', 'Volver a intentar la lectura')}>
                {movil ? i18nT('components.AccesoSoporteBanner.reintentar', 'Reintentar') : i18nT('components.AccesoSoporteBanner.volver_a_intentarlo', 'Volver a intentarlo')}
              </button>
            : null}>
          <div>{a.motivo}</div>
          {a.clase === 'ciego' && (
            <div style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>
              {i18nT('components.AccesoSoporteBanner.esto_no_quiere_decir_que', 'Esto NO quiere decir que no haya nadie dentro: quiere decir que esta pantalla no ha podido leerlo. Si el soporte técnico estaba trabajando en los datos de la clínica, sigue estándolo hasta que caduque o alguien lo corte.')}
              {/* LA SALIDA, Y TIENE QUE SER UNA QUE ABRA PARA QUIEN LA ESTÁ LEYENDO. Ver el bloque
                  E de la cabecera: aquí decía «se puede cortar desde Administración, en el Registro
                  de actividad» A TODO EL MUNDO, y esa puerta está cerrada justo para la persona que
                  se queda sola un sábado. */}
              <div data-salida-ciego={puedeRevisarElRegistro ? 'administracion' : 'equipo'}
                style={{ marginTop: '6px' }}>
                {tx('components.AccesoSoporteBanner.cortarlo_no_es_cosa_de', 'Cortarlo no es cosa de administración: lo puede hacer cualquiera del equipo desde este mismo aviso. Vuelve a intentar la lectura con el botón de esta franja — en cuanto se pueda leer, sale el botón de cortar el acceso.{{x}}', { x: puedeRevisarElRegistro
                  ? <>{i18nT('components.AccesoSoporteBanner.y_como_llevas_la_administracion', ' Y como llevas la administración de la clínica, la lista completa —quién entró, cuándo y hasta cuándo— está en ')}
                      <a data-ir-al-registro href="/registro-actividad" style={{ color: 'inherit', fontWeight: 700 }}>
                        {i18nT('components.AccesoSoporteBanner.el_registro_de_actividad', 'el Registro de actividad')}</a>{i18nT('components.AccesoSoporteBanner.y_tambien_se_corta_desde', ', y también se corta desde ahí.')}</>
                  : <>{i18nT('components.AccesoSoporteBanner.si_sigue_sin_poder_leerse', ' Si sigue sin poder leerse, avísale a alguien que lleve la administración de la clínica: esa lista completa solo la puede abrir esa persona.')}</> })}
              </div>
            </div>
          )}
        </Franja>
      ))}

      {vivos.map(f => (
        <Franja key={f.id} tono={f.emergencia ? 'emergencia' : 'vivo'} icono={f.emergencia ? 'warn' : 'lock'} movil={movil} marca
          linea={nombreCorto(f) ? i18nT('components.AccesoSoporteBanner.el_soporte_de_biango_esta_2', 'El soporte de Biango ({{nombreCorto}}) está dentro', { nombreCorto: nombreCorto(f) }) : i18nT('components.AccesoSoporteBanner.el_soporte_de_biango_esta_3', 'El soporte de Biango está dentro')}
          titulo={i18nT('components.AccesoSoporteBanner.el_soporte_de_biango_esta', 'El soporte de Biango está dentro de los datos de esta clínica')}
          abierta={estaAbierta('vivo-' + f.id, false)} alAlternar={alternar('vivo-' + f.id, false)} idDetalle={'acceso-soporte-vivo-' + f.id}
          accion={
            <button type="button" data-acceso-revocar={f.id} onClick={() => revocar(f)} disabled={ocupado === f.id} style={boton({ tipo: 'corta', movil })}
              title={i18nT('components.AccesoSoporteBanner.cortar_el_acceso_ahora_esa', 'Cortar el acceso ahora: esa sesión del soporte deja de ver los datos de la clínica')}>
              {ocupado === f.id ? i18nT('components.AccesoSoporteBanner.un_momento', 'Un momento…') : i18nT('components.AccesoSoporteBanner.cortar', 'Cortar')}
            </button>
          }>
          <div>{quienPide(f)}</div>
          <div><b>{i18nT('components.AccesoSoporteBanner.motivo', 'Motivo:')}</b> {f.motivo || i18nT('components.AccesoSoporteBanner.no_consta', '(no consta)')}</div>
          {/* LO QUE ES, DICHO SIN ASUSTAR Y SIN CALLAR NADA. El soporte entra sin pedir permiso (lo cubre el contrato de
              encargado del tratamiento); la clínica lo ve aquí, lo tiene apuntado en su registro y lo puede cortar. */}
          <div data-acceso-informativo>
            {tx('components.AccesoSoporteBanner.el_soporte_de_biango_puede', 'El soporte de Biango puede entrar en tu clínica para ayudarte; cada entrada queda en tu Registro de actividad. Puede hacer cambios para ayudarte, y cada cambio queda en el registro con su nombre, marcado como «Soporte de Biango». Lo clínico no lo toca: eso solo lo escribe tu equipo.{{x}}', { x: puedeRevisarElRegistro
              ? <>{i18nT('components.AccesoSoporteBanner.quien_ha_entrado_y_cuando', ' Quién ha entrado y cuándo está en ')}
                  <a data-ir-al-registro href="/registro-actividad" style={{ color: 'inherit', fontWeight: 700 }}>
                    {i18nT('components.AccesoSoporteBanner.el_registro_de_actividad', 'el Registro de actividad')}</a>.</>
              : <>{i18nT('components.AccesoSoporteBanner.el_registro_de_actividad_lo', ' El Registro de actividad lo abre quien lleva la administración de la clínica.')}</> })}
          </div>
          {/* Una sesión abierta ANTES del 1-oct-2026 puede venir autorizada por alguien de la clínica o por la puerta de
              emergencia de entonces: se cuenta como fue. */}
          {f.emergencia && <div>{i18nT('components.AccesoSoporteBanner.es_una_entrada_por_la', 'Es una entrada por la puerta de emergencia de antes: nadie de la clínica la autorizó.')}</div>}
          {!f.emergencia && f.directa !== true && f.resuelta_por_nombre && (
            <div>{tx('components.AccesoSoporteBanner.lo_autorizo', 'Lo autorizó {{resuelta_por_nombre}}{{x}}.', { resuelta_por_nombre: f.resuelta_por_nombre, x: f.resuelta ? i18nT('components.AccesoSoporteBanner.el', ' el {{resuelta}}', { resuelta: f.resuelta }) : '' })}</div>
          )}
          <div style={{ color: 'var(--text-secondary)' }}>
            {tx('components.AccesoSoporteBanner.la_sesion_se_apaga_sola', 'La sesión se apaga sola: {{x}}.', { x: cuandoCaduca(f.acceso_hasta) || i18nT('components.AccesoSoporteBanner.sin_fecha', 'sin fecha') })}
          </div>
        </Franja>
      ))}

      {notas.map(n => (
        <Franja key={n.id} tono={n.tono} icono={ICONO_NOTA[n.tono] || 'info'} movil={movil}
          linea={TITULO_NOTA[n.tono] || TITULO_NOTA.fallo}
          abierta={estaAbierta('nota-' + n.id, true)} alAlternar={alternar('nota-' + n.id, true)} idDetalle={'acceso-soporte-nota-' + n.id}
          accion={
            <button type="button" data-acceso-cerrar-nota={n.id} onClick={() => setRespuestas(r => { const c = { ...r }; delete c[n.id]; return c })}
              style={boton({ tipo: 'no', movil })}>{i18nT('components.AccesoSoporteBanner.entendido', 'Entendido')}</button>
          }>
          <div>{n.mensaje}</div>
          {n.fila && n.fila.resuelta_por_nombre && n.fila.estado !== 'pendiente' && (
            <div style={{ color: 'var(--text-secondary)' }}>
              {i18nT('components.AccesoSoporteBanner.quedo_como', 'Quedó como ')}<b>{n.fila.estado}</b>
              {tx('components.AccesoSoporteBanner.por', '{{x}} por {{resuelta_por_nombre}}.', { x: n.fila.resuelta ? i18nT('components.AccesoSoporteBanner.el', ' el {{resuelta}}', { resuelta: n.fila.resuelta }) : '', resuelta_por_nombre: n.fila.resuelta_por_nombre })}
            </div>
          )}
        </Franja>
      ))}
    </div>
  )
}
